import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../Components/Common/HelpArticleLink";
import { resolveCurrentTenantId } from "../../../helpers/agendaUnitContext";
import {
  dismissBrHolidaysReminder,
  isBrHolidaysReminderDismissed,
  shouldOfferBrHolidaysReminder,
} from "../../../helpers/brHolidaysReminder";
import { HELP_ARTICLE_CALENDAR_EXCEPTIONS } from "../../../helpers/helpCenter";
import {
  CalendarException,
  CalendarExceptionSource,
  CalendarExceptionType,
  createCalendarException,
  deleteCalendarException,
  getCalendarException,
  listCalendarExceptions,
  removeBrazilianHolidaysYear,
  removeBrazilianRegionalHolidaysYear,
  updateCalendarException,
} from "../../../services/calendarExceptionService";
import { getUnits, Unit } from "../../../services/unitService";
import { PaginationMeta } from "../../../types/api";
import CalendarExceptionFormModal, {
  CalendarExceptionFormValues,
  formatCalendarExceptionPeriod,
  resolveCalendarExceptionTypeLabel,
} from "./CalendarExceptionFormModal";
import { BrazilianHolidaysImportSummary } from "./ImportBrazilianHolidaysModal";
import { BrazilianRegionalHolidaysImportSummary } from "./ImportBrazilianRegionalHolidaysModal";
import ImportBrazilHolidaysWizardModal from "./ImportBrazilHolidaysWizardModal";

const PER_PAGE = 15;

const CalendarExceptionsPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  document.title = t("studio.settings.calendar_exceptions.pageTitle");

  const [exceptions, setExceptions] = useState<CalendarException[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [filterUnitId, setFilterUnitId] = useState("");
  const [filterType, setFilterType] = useState<CalendarExceptionType | "">("");
  const [filterSource, setFilterSource] = useState<CalendarExceptionSource | "">("");
  const [filterYear, setFilterYear] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importInitialTab, setImportInitialTab] = useState<"national" | "regional">("national");
  const [importInitialYear, setImportInitialYear] = useState<number | undefined>(undefined);
  const [draftValues, setDraftValues] = useState<Partial<CalendarExceptionFormValues> | null>(null);
  const [editingException, setEditingException] = useState<CalendarException | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CalendarException | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [removeImportedYear, setRemoveImportedYear] = useState(String(new Date().getFullYear()));
  const [removeImportedModal, setRemoveImportedModal] = useState(false);
  const [removingImported, setRemovingImported] = useState(false);
  const [reminderYear, setReminderYear] = useState<number | null>(null);
  const [reminderVisible, setReminderVisible] = useState(false);

  const tenantId = resolveCurrentTenantId();

  const fetchUnits = useCallback(async () => {
    try {
      const response = await getUnits({ per_page: 100 });
      setUnits(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.settings.calendar_exceptions.toast.loadError"));
    }
  }, [t]);

  const fetchExceptions = useCallback(async () => {
    setLoading(true);

    try {
      const response = await listCalendarExceptions({
        page,
        per_page: PER_PAGE,
        unit_id: filterUnitId ? Number(filterUnitId) : undefined,
        type: filterType || undefined,
        source: filterSource || undefined,
        year: filterYear ? Number(filterYear) : undefined,
      });

      setExceptions(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("studio.settings.calendar_exceptions.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [filterSource, filterType, filterUnitId, filterYear, page, t]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  useEffect(() => {
    fetchExceptions();
  }, [fetchExceptions]);

  useEffect(() => {
    setPage(1);
  }, [filterUnitId, filterType, filterSource, filterYear]);

  useEffect(() => {
    const yearParam = searchParams.get("year");
    const importParam = searchParams.get("import");
    const importRegionalParam = searchParams.get("import_regional");
    const scenarioParam = searchParams.get("scenario");
    const dateParam = searchParams.get("date");
    const exceptionIdParam = searchParams.get("exception_id");

    if (yearParam && /^\d{4}$/.test(yearParam)) {
      setFilterYear(yearParam);
      setImportInitialYear(Number(yearParam));
    }

    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) && !scenarioParam) {
      setFilterYear(dateParam.slice(0, 4));
    }

    if (importParam === "1") {
      setImportInitialTab("national");
      setImportModalOpen(true);
    }

    if (importRegionalParam === "1") {
      setImportInitialTab("regional");
      setImportModalOpen(true);
    }

    if (scenarioParam === "extended_hours" && dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      setEditingException(null);
      setDraftValues({
        scenario: "extended_hours",
        date: dateParam,
        start_time: "09:00",
        end_time: "22:00",
        reason: t("studio.settings.calendar_exceptions.brHolidays.extendedHours.christmasEveReason"),
      });
      setModalOpen(true);
    }

    const focusException = async () => {
      if (exceptionIdParam && /^\d+$/.test(exceptionIdParam)) {
        try {
          const response = await getCalendarException(Number(exceptionIdParam));
          setDraftValues(null);
          setEditingException(response.data);
          setModalOpen(true);
          return;
        } catch {
          // Fall through to date-based focus.
        }
      }

      if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) && scenarioParam !== "extended_hours") {
        try {
          const response = await listCalendarExceptions({
            date: dateParam,
            per_page: 20,
          });
          const negative = response.data.find((item) =>
            ["holiday", "closure", "event"].includes(item.type)
          );
          if (negative) {
            setDraftValues(null);
            setEditingException(negative);
            setModalOpen(true);
          }
        } catch {
          // Ignore deep-link focus errors; list still loads normally.
        }
      }
    };

    void focusException();

    if (yearParam || importParam || importRegionalParam || scenarioParam || dateParam || exceptionIdParam) {
      const next = new URLSearchParams(searchParams);
      next.delete("year");
      next.delete("import");
      next.delete("import_regional");
      next.delete("scenario");
      next.delete("date");
      next.delete("exception_id");
      setSearchParams(next, { replace: true });
    }
    // Apply deep-link params once on mount / first searchParams change intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const offeredYear = shouldOfferBrHolidaysReminder();
    if (!offeredYear || !tenantId) {
      setReminderVisible(false);
      return;
    }

    if (isBrHolidaysReminderDismissed(tenantId, offeredYear)) {
      setReminderVisible(false);
      return;
    }

    let cancelled = false;

    void listCalendarExceptions({
      year: offeredYear,
      source: "br_national_import",
      per_page: 1,
    })
      .then((response) => {
        if (cancelled) {
          return;
        }

        const hasImported = (response.meta?.total ?? response.data.length) > 0;
        setReminderYear(offeredYear);
        setReminderVisible(!hasImported);
      })
      .catch(() => {
        if (!cancelled) {
          setReminderVisible(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const openCreate = () => {
    setEditingException(null);
    setDraftValues(null);
    setModalOpen(true);
  };

  const openEdit = (exception: CalendarException) => {
    setDraftValues(null);
    setEditingException(exception);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingException(null);
    setDraftValues(null);
  };

  const handleSubmit = async (payload: Parameters<typeof createCalendarException>[0]) => {
    setSubmitting(true);

    try {
      if (editingException) {
        const response = await updateCalendarException(editingException.id, payload);
        toast.success(response.message || t("studio.settings.calendar_exceptions.toast.updateSuccess"));
      } else {
        const response = await createCalendarException(payload);
        toast.success(response.message || t("studio.settings.calendar_exceptions.toast.createSuccess"));
      }

      closeModal();

      if (!editingException && page !== 1) {
        setPage(1);
      } else {
        await fetchExceptions();
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.settings.calendar_exceptions.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      const response = await deleteCalendarException(deleteTarget.id);
      toast.success(response.message || t("studio.settings.calendar_exceptions.toast.deleteSuccess"));
      setDeleteModal(false);
      setDeleteTarget(null);

      const isLastItemOnPage = exceptions.length === 1 && page > 1;
      if (isLastItemOnPage) {
        setPage((current) => current - 1);
      } else {
        await fetchExceptions();
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.settings.calendar_exceptions.toast.deleteError"));
    }
  };

  const confirmRemoveImported = async () => {
    const year = Number(removeImportedYear);

    if (!Number.isInteger(year) || year < 2000 || year > 2100) {
      toast.error(t("studio.settings.calendar_exceptions.brHolidays.validation.yearInvalid"));
      return;
    }

    setRemovingImported(true);

    try {
      const [national, regional] = await Promise.all([
        removeBrazilianHolidaysYear(year),
        removeBrazilianRegionalHolidaysYear(year, { scope: "all" }),
      ]);
      const removed = national.data.removed + regional.data.removed;
      toast.success(
        t("studio.settings.calendar_exceptions.brHolidays.toast.removeSuccess", {
          count: removed,
          year,
        })
      );
      setRemoveImportedModal(false);
      await fetchExceptions();
    } catch (error: any) {
      toast.error(error?.data || t("studio.settings.calendar_exceptions.brHolidays.toast.removeError"));
    } finally {
      setRemovingImported(false);
    }
  };

  const offerChristmasEveSpecialHours = async (summary: BrazilianHolidaysImportSummary) => {
    const importedChristmas =
      summary.codes.includes("christmas") || summary.codes.includes("christmas_eve");

    if (!importedChristmas || summary.created < 1) {
      return;
    }

    const eveDate = `${summary.year}-12-24`;
    const result = await Swal.fire({
      icon: "question",
      title: t("studio.settings.calendar_exceptions.brHolidays.extendedHours.title"),
      text: t("studio.settings.calendar_exceptions.brHolidays.extendedHours.description"),
      showCancelButton: true,
      confirmButtonText: t("studio.settings.calendar_exceptions.brHolidays.extendedHours.confirm"),
      cancelButtonText: t("studio.settings.calendar_exceptions.brHolidays.extendedHours.dismiss"),
    });

    if (!result.isConfirmed) {
      return;
    }

    setEditingException(null);
    setDraftValues({
      scenario: "extended_hours",
      date: eveDate,
      start_time: "09:00",
      end_time: "22:00",
      reason: t("studio.settings.calendar_exceptions.brHolidays.extendedHours.christmasEveReason"),
    });
    setModalOpen(true);
  };

  const handleImported = async (summary: BrazilianHolidaysImportSummary) => {
    toast.success(
      t("studio.settings.calendar_exceptions.brHolidays.toast.importSummary", {
        created: summary.created,
        skipped: summary.skipped,
      })
    );

    if (tenantId && reminderYear === summary.year) {
      dismissBrHolidaysReminder(tenantId, summary.year);
      setReminderVisible(false);
    }

    if (page !== 1) {
      setPage(1);
    } else {
      await fetchExceptions();
    }

    await offerChristmasEveSpecialHours(summary);
  };

  const handleRegionalImported = async (summary: BrazilianRegionalHolidaysImportSummary) => {
    toast.success(
      t("studio.settings.calendar_exceptions.brRegional.toast.importSummary", {
        created: summary.created,
        skipped: summary.skipped,
        uf: summary.uf,
      })
    );

    if (tenantId && reminderYear === summary.year) {
      dismissBrHolidaysReminder(tenantId, summary.year);
      setReminderVisible(false);
    }

    if (page !== 1) {
      setPage(1);
    } else {
      await fetchExceptions();
    }
  };

  const sourceBadge = (source?: string) => {
    if (source === "br_national_import") {
      return (
        <Badge color="info" className="ms-1">
          {t("studio.settings.calendar_exceptions.source.imported")}
        </Badge>
      );
    }

    if (source === "br_state_import") {
      return (
        <Badge color="primary" className="ms-1">
          {t("studio.settings.calendar_exceptions.source.state")}
        </Badge>
      );
    }

    if (source === "br_city_import") {
      return (
        <Badge color="success" className="ms-1">
          {t("studio.settings.calendar_exceptions.source.city")}
        </Badge>
      );
    }

    return (
      <Badge color="light" className="ms-1 text-body">
        {t("studio.settings.calendar_exceptions.source.manual")}
      </Badge>
    );
  };

  const totalPages = meta?.last_page ?? 1;
  const yearOptions = useMemo(
    () => Array.from({ length: 6 }, (_, index) => new Date().getFullYear() - 1 + index),
    []
  );
  const hasUnitWithUf = useMemo(
    () => units.some((unit) => typeof unit.state === "string" && unit.state.trim().length === 2),
    [units]
  );

  const openImportWizard = (
    tab: "national" | "regional" = "national",
    yearOverride?: number
  ) => {
    setImportInitialTab(tab);
    setImportInitialYear(
      yearOverride ?? (filterYear ? Number(filterYear) : new Date().getFullYear())
    );
    setImportModalOpen(true);
  };

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("studio.settings.calendar_exceptions.breadcrumb.title")}
            pageTitle={t("studio.settings.calendar_exceptions.breadcrumb.pageTitle")}
          />
          <div className="d-flex justify-content-end mb-3">
            <HelpArticleLink articlePath={HELP_ARTICLE_CALENDAR_EXCEPTIONS} />
          </div>

          {reminderVisible && reminderYear ? (
            <Alert color="primary" className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <span>
                {t("studio.settings.calendar_exceptions.brHolidays.reminder.message", {
                  year: reminderYear,
                })}
              </span>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => {
                    openImportWizard(
                      hasUnitWithUf ? "regional" : "national",
                      reminderYear
                    );
                  }}
                >
                  {t("studio.settings.calendar_exceptions.brHolidays.reminder.cta", {
                    year: reminderYear,
                  })}
                </Button>
                <Button
                  color="light"
                  size="sm"
                  onClick={() => {
                    if (tenantId) {
                      dismissBrHolidaysReminder(tenantId, reminderYear);
                    }
                    setReminderVisible(false);
                  }}
                >
                  {t("studio.settings.calendar_exceptions.brHolidays.reminder.dismiss")}
                </Button>
              </div>
            </Alert>
          ) : null}

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                  <h4 className="card-title mb-0 flex-grow-1">
                    {t("studio.settings.calendar_exceptions.card.title")}
                  </h4>
                  <Button color="soft-primary" onClick={() => openImportWizard("national")}>
                    <i className="ri-calendar-event-line align-bottom me-1" />
                    {t("studio.settings.calendar_exceptions.brHolidays.actions.import")}
                  </Button>
                  <Button color="primary" onClick={openCreate}>
                    <i className="ri-add-line align-bottom me-1" />
                    {t("studio.settings.calendar_exceptions.actions.new")}
                  </Button>
                </CardHeader>

                <CardBody>
                  <p className="text-muted mb-3">
                    {t("studio.settings.calendar_exceptions.card.subtitle")}
                  </p>

                  <Row className="g-3 mb-3">
                    <Col md={3}>
                      <Input
                        type="select"
                        value={filterUnitId}
                        onChange={(event) => setFilterUnitId(event.target.value)}
                        aria-label={t("studio.settings.calendar_exceptions.filters.unit")}
                      >
                        <option value="">{t("studio.settings.calendar_exceptions.filters.allUnits")}</option>
                        {units.map((unit) => (
                          <option key={unit.id} value={unit.id}>
                            {unit.name}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={3}>
                      <Input
                        type="select"
                        value={filterType}
                        onChange={(event) =>
                          setFilterType(event.target.value as CalendarExceptionType | "")
                        }
                        aria-label={t("studio.settings.calendar_exceptions.filters.type")}
                      >
                        <option value="">{t("studio.settings.calendar_exceptions.filters.allTypes")}</option>
                        <option value="closure">
                          {t("studio.settings.calendar_exceptions.types.closure")}
                        </option>
                        <option value="holiday">
                          {t("studio.settings.calendar_exceptions.types.holiday")}
                        </option>
                        <option value="event">
                          {t("studio.settings.calendar_exceptions.types.event")}
                        </option>
                        <option value="extended_hours">
                          {t("studio.settings.calendar_exceptions.types.extended_hours")}
                        </option>
                      </Input>
                    </Col>
                    <Col md={3}>
                      <Input
                        type="select"
                        value={filterSource}
                        onChange={(event) =>
                          setFilterSource(event.target.value as CalendarExceptionSource | "")
                        }
                        aria-label={t("studio.settings.calendar_exceptions.filters.source")}
                      >
                        <option value="">
                          {t("studio.settings.calendar_exceptions.filters.allSources")}
                        </option>
                        <option value="manual">
                          {t("studio.settings.calendar_exceptions.source.manual")}
                        </option>
                        <option value="br_national_import">
                          {t("studio.settings.calendar_exceptions.source.imported")}
                        </option>
                        <option value="br_state_import">
                          {t("studio.settings.calendar_exceptions.source.state")}
                        </option>
                        <option value="br_city_import">
                          {t("studio.settings.calendar_exceptions.source.city")}
                        </option>
                      </Input>
                    </Col>
                    <Col md={3}>
                      <Input
                        type="select"
                        value={filterYear}
                        onChange={(event) => setFilterYear(event.target.value)}
                        aria-label={t("studio.settings.calendar_exceptions.filters.year")}
                      >
                        <option value="">
                          {t("studio.settings.calendar_exceptions.filters.allYears")}
                        </option>
                        {yearOptions.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </Input>
                    </Col>
                  </Row>

                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Button
                      color="soft-danger"
                      size="sm"
                      onClick={() => {
                        setRemoveImportedYear(filterYear || String(new Date().getFullYear()));
                        setRemoveImportedModal(true);
                      }}
                    >
                      {t("studio.settings.calendar_exceptions.brHolidays.actions.removeImported")}
                    </Button>
                  </div>

                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : exceptions.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-2">{t("studio.settings.calendar_exceptions.empty")}</p>
                      <p className="mb-4 small">
                        {t("studio.settings.calendar_exceptions.emptyHint")}
                      </p>
                      <div className="d-flex flex-wrap justify-content-center gap-2">
                        <Button color="primary" onClick={() => openImportWizard("national")}>
                          {t("studio.settings.calendar_exceptions.brHolidays.actions.import")}
                        </Button>
                        {hasUnitWithUf ? (
                          <Button
                            color="soft-success"
                            onClick={() => openImportWizard("regional")}
                          >
                            {t(
                              "studio.settings.calendar_exceptions.brRegional.actions.importEmpty"
                            )}
                          </Button>
                        ) : null}
                        <Button color="soft-primary" onClick={openCreate}>
                          {t("studio.settings.calendar_exceptions.actions.createFirst")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table hover className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("studio.settings.calendar_exceptions.table.type")}</th>
                              <th>{t("studio.settings.calendar_exceptions.table.origin")}</th>
                              <th>{t("studio.settings.calendar_exceptions.table.unit")}</th>
                              <th>{t("studio.settings.calendar_exceptions.table.period")}</th>
                              <th>{t("studio.settings.calendar_exceptions.table.reason")}</th>
                              <th className="text-end">
                                {t("studio.settings.calendar_exceptions.table.actions")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {exceptions.map((exception) => (
                              <tr key={exception.id}>
                                <td>
                                  <Badge color="secondary">
                                    {resolveCalendarExceptionTypeLabel(exception.type, t)}
                                  </Badge>
                                </td>
                                <td>{sourceBadge(exception.source)}</td>
                                <td>
                                  {exception.unit?.name ??
                                    t("studio.settings.calendar_exceptions.fields.allUnits")}
                                </td>
                                <td className="small">
                                  {formatCalendarExceptionPeriod(exception, i18n.language, t)}
                                </td>
                                <td>{exception.reason}</td>
                                <td className="text-end">
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    className="me-1"
                                    onClick={() => openEdit(exception)}
                                  >
                                    <i className="ri-pencil-line" />
                                  </Button>
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    onClick={() => {
                                      setDeleteTarget(exception);
                                      setDeleteModal(true);
                                    }}
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {meta && totalPages > 1 ? (
                        <Row className="align-items-center mt-3 g-3">
                          <Col sm={6}>
                            <p className="text-muted mb-0">
                              {t("studio.settings.calendar_exceptions.pagination.showing", {
                                from: meta.from ?? 0,
                                to: meta.to ?? 0,
                                total: meta.total,
                              })}
                            </p>
                          </Col>
                          <Col sm={6}>
                            <Pagination className="pagination-separated justify-content-sm-end mb-0">
                              <PaginationItem disabled={page <= 1}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page > 1) {
                                      setPage(page - 1);
                                    }
                                  }}
                                >
                                  {t("studio.settings.calendar_exceptions.pagination.previous")}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem active>
                                <PaginationLink href="#">
                                  {page} / {totalPages}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem disabled={page >= totalPages}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page < totalPages) {
                                      setPage(page + 1);
                                    }
                                  }}
                                >
                                  {t("studio.settings.calendar_exceptions.pagination.next")}
                                </PaginationLink>
                              </PaginationItem>
                            </Pagination>
                          </Col>
                        </Row>
                      ) : null}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <CalendarExceptionFormModal
        isOpen={modalOpen}
        editingException={editingException}
        draftValues={draftValues}
        units={units}
        submitting={submitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />

      <ImportBrazilHolidaysWizardModal
        isOpen={importModalOpen}
        units={units}
        submitting={submitting}
        initialYear={importInitialYear}
        initialTab={importInitialTab}
        onClose={() => setImportModalOpen(false)}
        onSubmittingChange={setSubmitting}
        onNationalImported={(summary) => {
          void handleImported(summary);
        }}
        onRegionalImported={(summary) => {
          void handleRegionalImported(summary);
        }}
      />

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger" />
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("studio.settings.calendar_exceptions.delete.title")}</h4>
              <p className="text-muted mx-4 mb-0">
                {deleteTarget?.reason
                  ? t("studio.settings.calendar_exceptions.delete.description", {
                      reason: deleteTarget.reason,
                    })
                  : t("studio.settings.calendar_exceptions.delete.fallback")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button color="light" onClick={() => setDeleteModal(false)}>
              {t("studio.settings.calendar_exceptions.actions.cancel")}
            </Button>
            <Button color="danger" onClick={confirmDelete}>
              {t("studio.settings.calendar_exceptions.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={removeImportedModal} toggle={() => setRemoveImportedModal(false)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-calendar-close-line display-5 text-danger" />
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("studio.settings.calendar_exceptions.brHolidays.remove.title")}</h4>
              <p className="text-muted mx-4 mb-3">
                {t("studio.settings.calendar_exceptions.brHolidays.remove.description")}
              </p>
              <Input
                type="select"
                value={removeImportedYear}
                onChange={(event) => setRemoveImportedYear(event.target.value)}
                className="mx-auto"
                style={{ maxWidth: 160 }}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Input>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button color="light" disabled={removingImported} onClick={() => setRemoveImportedModal(false)}>
              {t("studio.settings.calendar_exceptions.actions.cancel")}
            </Button>
            <Button color="danger" disabled={removingImported} onClick={confirmRemoveImported}>
              {removingImported ? <Spinner size="sm" className="me-1" /> : null}
              {t("studio.settings.calendar_exceptions.brHolidays.remove.confirm", {
                year: removeImportedYear,
              })}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default CalendarExceptionsPage;
